/**
 * Phone number formatting and parsing utilities
 * Uses libphonenumber-js for international phone number handling
 * Supports all countries with proper formatting based on their national standards
 */

import {
  parsePhoneNumber,
  isValidPhoneNumber,
  CountryCode,
  PhoneNumber,
} from 'libphonenumber-js';

/**
 * Format a phone number to international format with proper spacing
 * Examples:
 *   - Brazil: 5547991236370 → +55 47 99123-6370
 *   - US: 14155552671 → +1 415 555 2671
 *   - UK: 447911123456 → +44 7911 123456
 *   - Germany: 4930123456 → +49 30 123456
 *
 * @param phoneNumber - Phone number string (with or without + prefix)
 * @param defaultCountry - Default country code if not present in number (default: BR)
 * @returns Formatted phone number or original if parsing fails
 */
export const formatPhoneNumber = (
  phoneNumber: string | undefined | null,
  defaultCountry: CountryCode = 'BR'
): string => {
  if (!phoneNumber) return '';

  // Clean the input - remove non-numeric characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (!cleaned) return '';

  try {
    // Try to parse the number
    // If it doesn't start with +, add it for international format
    const numberToParse = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;

    const parsed = parsePhoneNumber(numberToParse);

    if (parsed && parsed.isValid()) {
      // Use international format for display
      return parsed.formatInternational();
    }

    // If parsing with + failed, try with default country
    const parsedWithCountry = parsePhoneNumber(cleaned, defaultCountry);

    if (parsedWithCountry && parsedWithCountry.isValid()) {
      return parsedWithCountry.formatInternational();
    }

    // Return cleaned number if all parsing fails
    return phoneNumber;
  } catch {
    // Return original if any error occurs
    return phoneNumber;
  }
};

/**
 * Format a phone number to national format (without country code)
 * Examples:
 *   - Brazil: 5547991236370 → (47) 99123-6370
 *   - US: 14155552671 → (415) 555-2671
 *
 * @param phoneNumber - Phone number string
 * @param defaultCountry - Default country code (default: BR)
 * @returns Formatted national phone number or original if parsing fails
 */
export const formatPhoneNational = (
  phoneNumber: string | undefined | null,
  defaultCountry: CountryCode = 'BR'
): string => {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (!cleaned) return '';

  try {
    const numberToParse = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
    const parsed = parsePhoneNumber(numberToParse);

    if (parsed && parsed.isValid()) {
      return parsed.formatNational();
    }

    const parsedWithCountry = parsePhoneNumber(cleaned, defaultCountry);

    if (parsedWithCountry && parsedWithCountry.isValid()) {
      return parsedWithCountry.formatNational();
    }

    return phoneNumber;
  } catch {
    return phoneNumber;
  }
};

/**
 * Validate if a string is a valid phone number
 *
 * @param phoneNumber - Phone number string to validate
 * @param country - Optional country code for validation
 * @returns True if valid phone number
 */
export const isValidPhone = (
  phoneNumber: string | undefined | null,
  country?: CountryCode
): boolean => {
  if (!phoneNumber) return false;

  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (!cleaned) return false;

  try {
    if (country) {
      return isValidPhoneNumber(cleaned, country);
    }

    // Try with + prefix for international numbers
    const numberToParse = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
    return isValidPhoneNumber(numberToParse);
  } catch {
    return false;
  }
};

/**
 * Extract country code from a phone number
 *
 * @param phoneNumber - Phone number string
 * @returns Country code (e.g., 'BR', 'US') or undefined if not found
 */
export const getPhoneCountry = (
  phoneNumber: string | undefined | null
): CountryCode | undefined => {
  if (!phoneNumber) return undefined;

  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (!cleaned) return undefined;

  try {
    const numberToParse = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
    const parsed = parsePhoneNumber(numberToParse);

    return parsed?.country;
  } catch {
    return undefined;
  }
};

/**
 * Get detailed phone number information
 *
 * @param phoneNumber - Phone number string
 * @param defaultCountry - Default country code (default: BR)
 * @returns Phone number details or null if invalid
 */
export const getPhoneDetails = (
  phoneNumber: string | undefined | null,
  defaultCountry: CountryCode = 'BR'
): {
  country: CountryCode | undefined;
  countryCallingCode: string | undefined;
  nationalNumber: string;
  international: string;
  national: string;
  isValid: boolean;
  type: string | undefined;
} | null => {
  if (!phoneNumber) return null;

  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (!cleaned) return null;

  try {
    const numberToParse = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
    let parsed: PhoneNumber | undefined = parsePhoneNumber(numberToParse);

    if (!parsed?.isValid()) {
      parsed = parsePhoneNumber(cleaned, defaultCountry);
    }

    if (!parsed) return null;

    return {
      country: parsed.country,
      countryCallingCode: parsed.countryCallingCode,
      nationalNumber: parsed.nationalNumber,
      international: parsed.formatInternational(),
      national: parsed.formatNational(),
      isValid: parsed.isValid(),
      type: parsed.getType(),
    };
  } catch {
    return null;
  }
};

/**
 * Format multiple phone numbers separated by comma
 *
 * @param phones - Array of phone numbers or comma-separated string
 * @param defaultCountry - Default country code (default: BR)
 * @returns Formatted phone numbers joined by comma
 */
export const formatPhoneNumbers = (
  phones: string[] | string | undefined | null,
  defaultCountry: CountryCode = 'BR'
): string => {
  if (!phones) return '';

  const phoneArray = Array.isArray(phones)
    ? phones
    : phones.split(',').map((p) => p.trim());

  return phoneArray
    .filter((p) => p)
    .map((p) => formatPhoneNumber(p, defaultCountry))
    .join(', ');
};
